import {CastorService} from '@aipb/gulliver/castor/data-access';
import {
  CastorError,
  CastorErrorCode,
  FormSearchParams,
  FormSolrSearchParams,
  Search,
  SearchSort,
  SolrSearchParams
} from '@aipb/gulliver/castor/model';
import {CatalogEnvService} from '@aipb/gulliver/catalog/env';
import {CustomRecordItem, ResultListMode, SearchItem, SearchResultSort, SearchTab} from '@aipb/gulliver/catalog/model';
import {
  CatalogXmlParserService,
  DEFAULT_HIGHLIGHTING_PARAMS,
  generateSearchId,
  mapHighlighting
} from '@aipb/gulliver/catalog/util';
import {CatalogGoogleAnalyticsService} from '@aipb/gulliver/util-google-analytics';
import {isString, truncate} from '@aipb/shared/util/string';
import {Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {captureException, withScope} from '@sentry/angular';
import {uniqBy} from 'lodash-es';
import isArray from 'lodash-es/isArray';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {CastorConfigRepository} from '../castor-config';
import {FacetRepository} from '../facet';
import {QueryService} from '../query.service';
import {SearchRepository} from './search.repository';
import {SnackbarService} from '@aipb/shared/util/snackbar';

export const RESULT_COUNT = 24;

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  constructor(
    private queryService: QueryService,
    private searchRepository: SearchRepository,
    private facetRepository: FacetRepository,
    private castor: CastorService,
    private catalogEnv: CatalogEnvService,
    private transloco: TranslocoService,
    private xmlParser: CatalogXmlParserService,
    private castorConfigRepository: CastorConfigRepository,
    private catalogGa: CatalogGoogleAnalyticsService,
    private snackbar: SnackbarService
  ) {}

  get configId() {
    return this.catalogEnv.activeConfigId;
  }

  public addTab(tab: SearchTab, active = false): void {
    const newTab: SearchTab = this.createSearchTab(tab);

    this.searchRepository.addTab(newTab);

    if (active) {
      this.setActiveTab(newTab.id);
    }
  }

  public fetchTabSearch(tab: SearchTab): Observable<Search<CustomRecordItem>> {
    const searchItem = this.searchRepository.getSearch(tab.id);

    if (searchItem) {
      return of(searchItem.search);
    }

    if (isString(tab.solrSearchParams.query)) {
      this.searchRepository.setLoading(true);
      return this.solrSearch({ ...(<SolrSearchParams>tab.solrSearchParams), queryParams: {
        ...tab.settings,
          defLogOperator: tab.defLogOperator
        } }).pipe(
        catchError((error) => {
          this.handleError(error, tab.id);

          return throwError(error);
        }),
        map((data) => this.mapSearch(data)),
        tap((data) => {
          this.add(tab.id, data, tab.solrSearchParams.coreId, tab.coordinates, tab.timelineRange);
          this.update(tab.id, data, tab.solrSearchParams.sort);
        }),
        finalize(() => this.searchRepository.setLoading(false))
      );
    }

    if (isArray(tab.solrSearchParams.query) && !(<FormSolrSearchParams>tab.solrSearchParams).solrQuery) {
      const params: FormSearchParams = {
        ...(<FormSearchParams>tab.solrSearchParams),
        queryParams: {
          ...tab.settings,
          defLogOperator: tab.defLogOperator
        }
      };

      if (this.catalogEnv.env.headerFormat === 'xml') {
        params.highlighting = DEFAULT_HIGHLIGHTING_PARAMS;
      }

      this.searchRepository.setLoading(true);

      return this.castor.formSearch<CustomRecordItem>(params).pipe(
        catchError((error) => {
          this.handleError(error, tab.id);

          return throwError(error);
        }),
        map((data) => this.mapSearch(data)),
        tap((data) => {
          this.add(tab.id, data, tab.solrSearchParams.coreId, tab.coordinates, tab.timelineRange);
          this.update(tab.id, data, tab.solrSearchParams.sort);
        }),
        finalize(() => this.searchRepository.setLoading(false))
      );
    }

    if (isArray(tab.solrSearchParams.query) && (<FormSolrSearchParams>tab.solrSearchParams).solrQuery) {
      const params: FormSolrSearchParams = {
        ...(<FormSolrSearchParams>tab.solrSearchParams),
        queryParams: {
          ...tab.settings,
          defLogOperator: tab.defLogOperator
        }
      };

      this.searchRepository.setLoading(true);

      return this.castor.formSolrSearch<CustomRecordItem>(params).pipe(
        catchError((error) => {
          this.handleError(error, tab.id);

          return throwError(error);
        }),
        map((data) => this.mapSearch(data)),
        tap((data) => {
          this.add(tab.id, data, tab.solrSearchParams.coreId, tab.coordinates, tab.timelineRange);
          this.update(tab.id, data, tab.solrSearchParams.sort);
        }),
        finalize(() => this.searchRepository.setLoading(false))
      );
    }

    return of(null);
  }

  public paginate(searchId: string, pageNum: number): void {
    const search = this.searchRepository.getSearch(searchId);

    const params: SolrSearchParams = {
      query: search.search.q,
      start: (pageNum < 0 ? 0 : pageNum) * search.search.pageSize,
      rows: search.search.pageSize,
      sort: search.search_sort,
      coreId: search.coreId
    };

    if (this.catalogEnv.hasMapSearch() && search.coordinates) {
      params.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: `${search.coordinates[0]},${search.coordinates[1]},${search.coordinates[2]},${search.coordinates[3]}`
      };
    }

    this.searchRepository.setLoading(true);
    this.solrSearch(params)
      .pipe(
        finalize(() => {
          this.searchRepository.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.update(search.id, data, search.search_sort);
      });
  }

  public sort(searchId: string, params: SearchResultSort[]): Observable<Search<CustomRecordItem>> {
    const search = this.searchRepository.getSearch(searchId);

    const sort = uniqBy(
      [
        ...params
          .filter((f) => f.field)
          .map((data) => {
            return {
              field: data.field.fieldId,
              dir: data.dir,
              lang: 'uni'
            };
          }),
        ...this.catalogEnv.env.sort
      ],
      (item) => item.field
    );

    this.catalogGa.SEARCH_RESULTS.sort(sort.map((d) => `${d.field}  ${d.dir} ${d.lang}`).join(','));

    const solrParams: SolrSearchParams = {
      query: search.search.q,
      start: 0,
      rows: search.search.pageSize,
      sort: sort,
      coreId: search.coreId
    };

    if (this.catalogEnv.hasMapSearch() && search.coordinates) {
      solrParams.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: `${search.coordinates[0]},${search.coordinates[1]},${search.coordinates[2]},${search.coordinates[3]}`
      };
    }

    this.searchRepository.setLoading(true);
    return this.solrSearch(solrParams).pipe(
      tap((data) => {
        this.update(search.id, data, sort);
      }),
      finalize(() => {
        this.searchRepository.setLoading(false);
      })
    );
  }

  public remove(id: string): void {
    this.searchRepository.removeSearch(id);
  }

  public removeAll(): void {
    this.searchRepository.removeSearches();
    this.searchRepository.removeTabs();
  }

  public toggleFormSetting(key: string): void {
    this.searchRepository.toggleFormSetting(key);
  }

  public toggleSearchSetting(key: string): void {
    this.searchRepository.toggleSearchSetting(key);
    this.catalogGa.SEARCH_FORM.searchSettingsToggle(key);
  }

  public toggleShowSearchSettings(): void {
    this.searchRepository.toggleShowSearchSettings();
  }

  public hideFacetList(): void {
    this.searchRepository.setShowFilters(false);
  }

  public toggleShowFacetList(): void {
    this.searchRepository.toggleShowFilters();
  }

  public setTabsLocked(tabsLocked = true): void {
    this.searchRepository.setTabsLocked(tabsLocked);
  }

  public setResultListMode(mode: ResultListMode): void {
    if (!this.catalogEnv.env.enableResultListMode) {
      return;
    }

    this.searchRepository.setResultListMode(mode);
  }

  public updateFacetResultCount(item: SearchItem, query: string, coordinates: number[] = undefined): void {
    const params: SolrSearchParams = {
      query: query,
      coreId: item.coreId,
      queryParams: {}
    };

    if (this.catalogEnv.hasMapSearch() && (coordinates || item.coordinates)) {
      params.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: coordinates
          ? `${coordinates[1]},${coordinates[0]},${coordinates[3]},${coordinates[2]}`
          : `${item.coordinates[0]},${item.coordinates[1]},${item.coordinates[2]},${item.coordinates[3]}`
      };
    }

    this.facetRepository.setLoading(true);
    this.solrSearch(params)
      .pipe(
        finalize(() => {
          this.facetRepository.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.facetRepository.setResultCount(item.id, data.found);
      });
  }

  public updateFacetNotResultCount(item: SearchItem, query: string, coordinates: number[] = undefined): void {
    const params: SolrSearchParams = {
      query: query,
      coreId: item.coreId,
      queryParams: {}
    };

    if (this.catalogEnv.hasMapSearch() && (coordinates || item.coordinates)) {
      params.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: coordinates
          ? `${coordinates[1]},${coordinates[0]},${coordinates[3]},${coordinates[2]}`
          : `${item.coordinates[0]},${item.coordinates[1]},${item.coordinates[2]},${item.coordinates[3]}`
      };
    }

    this.facetRepository.setLoading(true);
    this.solrSearch(params)
      .pipe(
        finalize(() => {
          this.facetRepository.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.facetRepository.setNotResultCount(item.id, data.found);
      });
  }

  public updateFacetStatResultCount(item: SearchItem, query: string, coordinates: number[] = undefined): void {
    const params: SolrSearchParams = {
      query: query,
      coreId: item.coreId,
      queryParams: {}
    };

    if (this.catalogEnv.hasMapSearch() && (coordinates || item.coordinates)) {
      params.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: coordinates
          ? `${coordinates[1]},${coordinates[0]},${coordinates[3]},${coordinates[2]}`
          : `${item.coordinates[0]},${item.coordinates[1]},${item.coordinates[2]},${item.coordinates[3]}`
      };
    }

    this.facetRepository.setLoading(true);
    this.solrSearch(params)
      .pipe(
        finalize(() => {
          this.facetRepository.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.facetRepository.setStatResultCount(item.id, data.found);
      });
  }

  public updateFacetTreeResultCount(item: SearchItem, query: string, coordinates: number[] = undefined): void {
    const params: SolrSearchParams = {
      query: query,
      coreId: item.coreId,
      queryParams: {}
    };

    if (this.catalogEnv.hasMapSearch() && (coordinates || item.coordinates)) {
      params.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: coordinates
          ? `${coordinates[1]},${coordinates[0]},${coordinates[3]},${coordinates[2]}`
          : `${item.coordinates[0]},${item.coordinates[1]},${item.coordinates[2]},${item.coordinates[3]}`
      };
    }

    this.facetRepository.setLoading(true);
    this.solrSearch(params)
      .pipe(
        finalize(() => {
          this.facetRepository.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.facetRepository.setTreeResultCount(item.id, data.found);
      });
  }

  public updateTimelineResultCount(item: SearchItem, query: string, coordinates: number[] = undefined): void {
    const params: SolrSearchParams = {
      query: query,
      coreId: item.coreId,
      queryParams: {}
    };

    if (this.catalogEnv.hasMapSearch() && (coordinates || item.coordinates)) {
      params.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: coordinates
          ? `${coordinates[1]},${coordinates[0]},${coordinates[3]},${coordinates[2]}`
          : `${item.coordinates[0]},${item.coordinates[1]},${item.coordinates[2]},${item.coordinates[3]}`
      };
    }

    this.facetRepository.setLoading(true);
    this.solrSearch(params)
      .pipe(
        finalize(() => {
          this.facetRepository.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.facetRepository.setTimelineResultCount(item.id, data.found);
      });
  }

  public updateMapResultCount(item: SearchItem, query: string, coordinates: number[] = undefined): void {
    const params: SolrSearchParams = {
      query: query,
      coreId: item.coreId,
      queryParams: {}
    };

    if (this.catalogEnv.hasMapSearch() && (coordinates || item.coordinates)) {
      params.spatialFilter = {
        spatialField: 'Coordinates',
        spatialType: 'rectangle',
        rectangle: coordinates
          ? `${coordinates[1]},${coordinates[0]},${coordinates[3]},${coordinates[2]}`
          : `${item.coordinates[0]},${item.coordinates[1]},${item.coordinates[2]},${item.coordinates[3]}`
      };
    }

    this.facetRepository.setLoading(true);
    this.solrSearch(params)
      .pipe(
        finalize(() => {
          this.facetRepository.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.facetRepository.setMapResultCount(item.id, data.found);
      });
  }

  public setActiveTabIndex(i: number) {
    this.searchRepository.setActiveTabIndex(i);
  }

  public solrSearch(params?: SolrSearchParams): Observable<Search<CustomRecordItem>> {
    const coreId = this.castorConfigRepository.getActiveCoreId();
    const solrSearchParams: SolrSearchParams = {
      coreId: coreId,
      rows: RESULT_COUNT,
      highlighting: DEFAULT_HIGHLIGHTING_PARAMS,
      sort: this.catalogEnv.env.sort,
      fields: this.catalogEnv.env.castor.requiredFields,
      ...params
    };

    if (this.catalogEnv.env.headerFormat === 'xml') {
      solrSearchParams.highlighting = DEFAULT_HIGHLIGHTING_PARAMS;
    }

    return this.castor.solrSearch<CustomRecordItem>(solrSearchParams).pipe(map((data) => this.mapSearch(data)));
  }

  private mapSearch(data: Search<CustomRecordItem>): Search<CustomRecordItem> {
    let lastLabel = null;
    let lastSublabel = null;
    let lastAdditional = null;

    data.recordList.forEach((d) => {
      try {
        if(this.catalogEnv.env.headerFormat === 'html') {
          d.header = <any>{
            label: {
              value: d.GulliverTabName
            }
          }
        }

        if (this.catalogEnv.env.headerFormat === 'xml') {
          d.header = this.xmlParser.parseCastorHeader(d[this.catalogEnv.env.castor.UUID], d.GulliverRecHeader);
        }

        if (this.catalogEnv.env.headerFormat === 'json') {
          d.header = JSON.parse(d.GulliverRecHeader);
        }

        d.header.id = d[this.catalogEnv.env.castor.UUID];

        d.details = [];

        if (d.GulliverRecDetail) {
          if (isArray(d.GulliverRecDetail)) {
            for (const detail of d.GulliverRecDetail) {
              d.details.push(JSON.parse(detail));
            }
          }

          if (isString(d.GulliverRecDetail)) {
            d.details.push(JSON.parse(d.GulliverRecDetail));
          }
        }

        d.fetch = this.xmlParser.parseCastorFetch3if(d.GulliverFetch3if);

        d.showLabel = true;
        d.showSublabel = true;
        d.showAdditional = true;

        if (!this.catalogEnv.env.fulltext) return;

        if (d.header.label && lastLabel === d.header.label.value) {
          d.showLabel = false;
        }

        if (d.header.label && lastLabel !== d.header.label.value) {
          lastLabel = d.header.label.value;
        }

        if (d.header.sublabel && lastSublabel === d.header.sublabel.value) {
          d.showSublabel = false;
        }

        if (d.header.sublabel && lastSublabel !== d.header.sublabel.value) {
          lastSublabel = d.header.sublabel.value;
        }

        if (d.header.additional && lastAdditional === d.header.additional.value) {
          d.showAdditional = false;
        }

        if (d.header.additional && lastAdditional !== d.header.additional.value) {
          lastAdditional = d.header.additional.value;
        }
      } catch (e) {
        withScope((scope) => {
          scope.setExtra('CASTOR_ID', d[this.catalogEnv.env.castor.UUID]);
          scope.setExtra('CASTOR_DATA', d);
          captureException(e);
        });
      }
    });

    return data;
  }

  private update(id: string, search: Search<CustomRecordItem>, sort: SearchSort[] = this.catalogEnv.env.sort): void {
    const s = this.searchRepository.getSearch(id);
    this.searchRepository.updateSearch(id, {
      search_at: Date.now(),
      search: {
        ...search,
        highlighting: mapHighlighting(search.highlighting)
      },
      search_sort: sort,
      parsedQuery: this.queryService.parseQuery(s.coreId, search.q)
    });

    this.searchRepository.updateSearchUI(id, s, search, sort);
  }

  private setActiveTab(id: string) {
    const tabs = this.searchRepository.getTabs();

    this.setActiveTabIndex(tabs.findIndex((t) => t.id === id));
  }

  private add(
    searchId: string,
    search: Search<CustomRecordItem>,
    coreId?: string,
    coordinates: number[] = null,
    timelineRange: string[] = null
  ): void {
    const searchItem = this.createSearchItem(searchId, search, coreId, coordinates, timelineRange);

    this.searchRepository.addSearch(searchItem);
  }

  private createSearchItem(
    searchId: string,
    search: Search<CustomRecordItem>,
    coreId?: string,
    coordinates: number[] = null,
    timelineRange: string[] = null
  ): SearchItem {
    const searchSettings = this.searchRepository.getSearchSettings();

    coreId = coreId || this.castorConfigRepository.getActiveCoreId();
    const core = this.castorConfigRepository.getCores().find((c) => c.coreId === coreId);
    const coreName = core ? core.coreName : '';

    search.highlighting = mapHighlighting(search.highlighting);

    return {
      id: searchId,
      search_at: Date.now(),
      search: search,
      search_sort: this.catalogEnv.env.sort,
      search_settings: searchSettings,
      parsedQuery: this.queryService.parseQuery(coreId, search.q),
      prettyQuery: truncate(this.queryService.getPrettyQuery(coreId, search.q), this.catalogEnv.env.searchTabMaxLength),
      coreId: coreId,
      coreName: coreName,
      lang: this.transloco.getActiveLang(),
      coordinates: coordinates,
      timelineRange: timelineRange
    };
  }

  private createSearchTab(tab: SearchTab): SearchTab {
    return {
      ...tab,
      id: tab.id || generateSearchId(),
      resultCount: tab.resultCount || null,
      coordinates: tab.coordinates || null,
      settings: tab.settings || this.searchRepository.getSearchSetttings(),
      defLogOperator: tab.defLogOperator || this.castorConfigRepository.activeLogOperator,
      solrSearchParams: {
        ...tab.solrSearchParams,
        coreId: tab.solrSearchParams.coreId || this.castorConfigRepository.getActiveCoreId(),
        sort: tab.solrSearchParams.sort || this.catalogEnv.env.sort,
        rows: tab.solrSearchParams.rows || RESULT_COUNT,
        highlighting: {
          ...tab.solrSearchParams.highlighting,
          ...(this.catalogEnv.env?.fulltext?.highlighting || {})
        },
        fields: tab.solrSearchParams.fields || this.catalogEnv.env.castor.requiredFields,
        start: tab.solrSearchParams.start || 0
      }
    };
  }

  private handleError(error, id) {
    if (error instanceof CastorError && error.code) {
      const hasTab = this.searchRepository.hasTab(id);

      if (hasTab) {
        this.remove(id);
      }

      if (error.code === CastorErrorCode.INSUFFICIENT_AUTHORIZATION) {
        this.snackbar.open(this.transloco.translate('catalog.messages.castor_search_not_authorized'), 'warn', {
          duration: 5000
        });
      }
    }
  }
}
